import { Button, Checkbox, Input, InputSelect, Label } from '@features/ui';
import { useCountryOptions } from '@hooks/useCountryOptions';
import { useStateOptions } from '@hooks/useStateOptions';
import { PaymentFormValues } from '@src/types/packages';
import { FC } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface BillingFormProps {
  form: UseFormReturn<PaymentFormValues>;
  submitting?: boolean;
  onSubmit?: () => void;
  showSubmitButton?: boolean;
  submitButtonText?: string;
  enableAboutSection?: boolean;
}

const paymentData = [
  {
    value: 'stripe',
    label: 'Stripe',
    url: '/assets/stripe.png',
    width: 60,
    height: 24,
  },
  {
    value: 'paypal',
    label: 'PayPal',
    url: '/assets/paypal.png',
    width: 60,
    height: 24,
  },
];

export const BillingForm: FC<BillingFormProps> = ({
  form,
  submitting,
  onSubmit,
  showSubmitButton = true,
  submitButtonText = 'Checkout',
  enableAboutSection = false,
}) => {
  const { t } = useTranslation();
  const { countryOptions } = useCountryOptions();
  const { stateOptions } = useStateOptions();

  const {
    control,
    watch,
    resetField,
    formState: { errors },
  } = form;

  const country = watch('country');

  return (
    <div className="space-y-4 lg:space-y-6 pl-4 pb-8 lg:pb-0 lg:pl-0 flex-1">
      <div>
        <h3 className="mb-4 text-[26px] font-medium">
          {t('checkout.paymentTerm')}
        </h3>

        <Controller
          control={control}
          name="payment_type"
          render={({ field }) => (
            <div className="space-y-4">
              {paymentData.map((item) => (
                <div key={item.value} className="flex items-center space-x-2">
                  <Checkbox
                    checked={field.value === item.value}
                    className="w-[22px] h-[22px] border-2 border-white"
                    id={item.value}
                    onCheckedChange={() => field.onChange(item.value)}
                  />
                  <Label
                    className="flex items-center gap-2 text-[16px] ml-3"
                    htmlFor={item.value}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        alt={`${item.label}-payment`}
                        height={item.height}
                        src={item.url}
                        width={item.width}
                      />
                      <span>{item.label}</span>
                    </div>
                  </Label>
                </div>
              ))}
              {errors.payment_type && (
                <p className="mt-1 text-xs text-red-500">
                  {errors.payment_type.message}
                </p>
              )}
            </div>
          )}
        />
      </div>

      <div className="mt-11 space-y-5">
        {/* Coupon Code */}
        <div className="space-y-2">
          <Label className="text-sm" htmlFor="coupon_code">
            {t('checkout.form.enterCoupon')}
          </Label>
          <Controller
            control={control}
            name="coupon_code"
            render={({ field }) => (
              <div>
                <Input
                  {...field}
                  endIcon={null}
                  id="coupon_code"
                  inputSize={'lg'}
                  placeholder={t('checkout.form.enterCouponPlaceholder')}
                  textSize={'sm'}
                  onBlur={() => {}}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                  }}
                />
                {errors.coupon_code && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.coupon_code.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* First Name & Last Name */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label
              className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
              htmlFor="first_name"
            >
              {t('checkout.form.firstName')}
            </Label>
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <div>
                  <Input
                    id="first_name"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.firstName')}
                    textSize={'sm'}
                    {...field}
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.first_name.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>

          <div className="space-y-2">
            <Label
              className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
              htmlFor="last_name"
            >
              {t('checkout.form.lastName')}
            </Label>
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <div>
                  <Input
                    id="last_name"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.lastName')}
                    textSize={'sm'}
                    {...field}
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.last_name.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Street */}
        <div className="space-y-2">
          <Label
            className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
            htmlFor="street"
          >
            {t('checkout.form.street')}
          </Label>
          <Controller
            control={control}
            name="street"
            render={({ field }) => (
              <div>
                <Input
                  id="street"
                  inputSize={'lg'}
                  placeholder={t('checkout.form.streetPlaceholder')}
                  textSize={'sm'}
                  {...field}
                />
                {errors.street && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.street.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* City */}
        <div className="space-y-2">
          <Label
            className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
            htmlFor="city"
          >
            {t('checkout.form.city')}
          </Label>
          <Controller
            control={control}
            name="city"
            render={({ field }) => (
              <div>
                <Input
                  id="city"
                  inputSize={'lg'}
                  placeholder={t('checkout.form.cityPlaceholder')}
                  textSize={'sm'}
                  {...field}
                />
                {errors.city && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.city.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* Country */}
        <div className="space-y-2">
          <Label
            className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
            htmlFor="country"
          >
            {t('checkout.form.country')}
          </Label>
          <Controller
            control={control}
            name="country"
            render={({ field }) => (
              <div>
                <InputSelect
                  borderColor={'border-[#979797]'}
                  className={'rounded-[10px] h-[42px] text-sm'}
                  options={countryOptions}
                  placeholder={t('checkout.form.cityPlaceholder')}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    resetField('state');
                    resetField('zip_code');
                  }}
                />
                {errors.country && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.country.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* State (US only) */}
        <div className={country !== 'US' ? 'hidden' : 'block'}>
          <div className="space-y-2">
            <Label
              className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
              htmlFor="state"
            >
              {t('checkout.form.state')}
            </Label>
            <Controller
              control={control}
              name="state"
              render={({ field }) => (
                <div>
                  <InputSelect
                    borderColor={'border-[#979797]'}
                    className={'rounded-[10px] h-[42px] text-sm'}
                    options={stateOptions}
                    placeholder={t('checkout.form.statePlaceholder')}
                    value={field.value ?? ''}
                    onChange={(value) => field.onChange(value)}
                  />
                  {errors.state && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.state.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Zip Code (US only) */}
        <div className={country !== 'US' ? 'hidden' : 'block'}>
          <div className="space-y-2">
            <Label
              className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
              htmlFor="zip_code"
            >
              {t('checkout.form.zipCode')}
            </Label>
            <Controller
              control={control}
              name="zip_code"
              render={({ field }) => (
                <div>
                  <Input
                    {...field}
                    id="zip_code"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.zipCodePlaceholder')}
                    textSize={'sm'}
                    value={field.value ?? ''}
                  />
                  {errors.zip_code && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.zip_code.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <Label
            className="text-sm after:ml-0.5 after:text-red-500 after:content-['*']"
            htmlFor="phone_number"
          >
            {t('checkout.form.phoneNumber')}
          </Label>
          <Controller
            control={control}
            name="phone_number"
            render={({ field }) => (
              <div>
                <Input
                  id="phone_number"
                  inputSize={'lg'}
                  placeholder={t('checkout.form.phoneNumberPlaceholder')}
                  textSize={'sm'}
                  {...field}
                />
                {errors.phone_number && (
                  <p className="mt-1 text-xs text-red-500">
                    {errors.phone_number.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* Referral Source */}
        {enableAboutSection && (
          <div className="space-y-2">
            <Label className="text-sm" htmlFor="referral_source">
              {t('checkout.form.hearAboutUs')}
            </Label>
            <Controller
              control={control}
              name="referral_source"
              render={({ field }) => (
                <div>
                  <Input
                    id="referral_source"
                    inputSize={'lg'}
                    placeholder={t('checkout.form.hearAboutUsPlaceholder')}
                    textSize={'sm'}
                    {...field}
                  />
                  {errors.referral_source && (
                    <p className="mt-1 text-xs text-red-500">
                      {errors.referral_source.message}
                    </p>
                  )}
                </div>
              )}
            />
          </div>
        )}

        {/* Submit Button */}
        {showSubmitButton && (
          <Button
            className="w-full"
            disabled={submitting}
            size="lg"
            type="submit"
            variant="gradient"
            onClick={onSubmit}
          >
            {submitting ? 'Processing…' : submitButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};
